// Contabilidad: los empleados del estudio con sus liquidaciones, contratos y
// anexos, el cálculo de la liquidación del mes (para compararla con la de la
// contadora) y el historial de todo lo que se hizo. Ver utils/contabilidad.ts.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as FS from '../../utils/datos';
import * as CT from '../../utils/contabilidad';
import { nombreMes } from '../../utils/periodos';

const TIPOS: Record<CT.Empleado['tipoContrato'], string> = { indefinido: 'Indefinido', plazo_fijo: 'Plazo fijo', obra_faena: 'Por obra o faena' };
const GRATIF: Record<CT.Empleado['gratificacion'], string> = { art50: 'Art. 50 (25 %, con tope)', art47: 'Art. 47 (30 % de utilidades)', ninguna: 'Sin gratificación' };
const ACCIONES: Record<string, string> = {
  crear_empleado: 'agregó al empleado',
  editar_empleado: 'cambió los datos del empleado',
  subir_documento_laboral: 'subió',
  ver_documento_laboral: 'abrió',
  eliminar_documento_laboral: 'borró',
};

const mesActual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const etiquetaMes = (p: string | null) => { if (!p) return ''; const [a, m] = p.split('-').map(Number); return `${nombreMes(m - 1)} ${a}`; };
const cuando = (iso: string) => new Date(iso).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const soloDigitos = (v: string) => v.replace(/\D/g, '');
const aNumero = (v: string) => (soloDigitos(v) ? Number(soloDigitos(v)) : null);
const singular = (c: CT.ClaseDocumento) => CT.CLASES.find((x) => x.id === c)?.singular || c;

/** Cargo sugerido según el rol del usuario en Tabularis. */
const CARGO_POR_ROL: Record<string, string> = {
  admin: 'Administración', admin_no_abogado: 'Administración', abogada: 'Abogada', abogado_asociado: 'Abogado asociado',
  captadora: 'Captadora', captadora_general: 'Captadora general', procuradora: 'Procuradora', abogado_externo: 'Abogado externo',
};
type Usuario = { id: string; nombre: string; email: string; rol: string; activo: boolean };
const OTRA = '__otra';

const caja: React.CSSProperties = { padding: 16, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', marginBottom: 16 };
const claro = '#F0F4FF';

const Contabilidad: React.FC = () => {
  const [vista, setVista] = useState<'empleados' | 'previred' | 'historial'>('empleados');
  const [empleados, setEmpleados] = useState<CT.Empleado[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  useEffect(() => { FS.loadUsuarios().then((us: Usuario[]) => setUsuarios(us)).catch(() => setUsuarios([])); }, []);

  const cargar = useCallback(async () => {
    try { setEmpleados(await CT.listarEmpleados()); setError(''); }
    catch (e: any) { setError(e?.message || 'No se pudo cargar'); }
    finally { setCargando(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const sel = empleados.find((e) => e.id === selId) || null;

  return (
    <div className="p-section">
      <div className="p-section-title">Contabilidad</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -6, marginBottom: 16 }}>
        Liquidaciones, contratos y anexos de los empleados del estudio. Todo lo que se sube, abre o borra queda en el historial.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        <button className={vista === 'empleados' ? 'p-btn-primary' : 'p-btn-secondary'} onClick={() => setVista('empleados')} style={{ padding: '8px 16px', fontSize: 13 }}>👥 Empleados</button>
        <button className={vista === 'previred' ? 'p-btn-primary' : 'p-btn-secondary'} onClick={() => setVista('previred')} style={{ padding: '8px 16px', fontSize: 13 }}>🏛️ Previred del mes</button>
        <button className={vista === 'historial' ? 'p-btn-primary' : 'p-btn-secondary'} onClick={() => setVista('historial')} style={{ padding: '8px 16px', fontSize: 13 }}>🕓 Historial</button>
      </div>
      {error && <div style={{ color: '#e57373', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {vista === 'historial' && <Historial />}
      {vista === 'previred' && <PreviredMes empleados={empleados} />}

      {vista === 'empleados' && (cargando ? <div className="p-empty">Cargando…</div> : <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, marginBottom: 18 }}>
          {empleados.map((e) => (
            <button key={e.id} onClick={() => { setSelId(e.id); setNuevo(false); }}
              style={{ ...caja, marginBottom: 0, textAlign: 'left', cursor: 'pointer', color: claro, opacity: e.activo ? 1 : 0.55, borderColor: selId === e.id ? '#8FB4E8' : 'rgba(255,255,255,0.12)' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{e.nombre}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {e.cargo || 'Sin cargo'} · {TIPOS[e.tipoContrato]}{!e.activo && ' · inactivo'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{e.documentos ?? 0} {(e.documentos ?? 0) === 1 ? 'documento' : 'documentos'}</div>
            </button>
          ))}
          <button onClick={() => { setNuevo(true); setSelId(null); }} className="p-btn-secondary" style={{ minHeight: 84, fontSize: 13 }}>＋ Agregar empleado</button>
        </div>
        {empleados.length === 0 && !nuevo && <div className="p-empty">Aún no hay empleados. Agrega el primero para empezar a guardar sus liquidaciones.</div>}
        {nuevo && <FichaEmpleado key="nuevo" usuarios={usuarios} empleados={empleados} onGuardado={async (e) => { await cargar(); setNuevo(false); setSelId(e.id); }} onCancelar={() => setNuevo(false)} />}
        {sel && <Empleado key={sel.id} empleado={sel} usuarios={usuarios} empleados={empleados} onCambio={cargar} />}
      </>)}
    </div>
  );
};

/** Datos del empleado: lo que necesita la liquidación. */
const FichaEmpleado: React.FC<{ empleado?: CT.Empleado; usuarios: Usuario[]; empleados: CT.Empleado[]; onGuardado: (e: CT.Empleado) => void; onCancelar?: () => void }> = ({ empleado, usuarios, empleados, onGuardado, onCancelar }) => {
  // El trabajador se elige entre los usuarios del estudio. Los que ya son
  // empleados no se ofrecen de nuevo; "otra persona" es para quien trabaja en
  // el estudio sin usar Tabularis.
  const [usuarioId, setUsuarioId] = useState(empleado ? empleado.usuarioId || OTRA : '');
  const tomados = new Set(empleados.filter((x) => x.usuarioId && x.id !== empleado?.id).map((x) => x.usuarioId));
  const elegibles = usuarios.filter((u) => (u.activo || u.id === empleado?.usuarioId) && !tomados.has(u.id));
  const [f, setF] = useState({
    nombre: empleado?.nombre || '', rut: empleado?.rut || '', cargo: empleado?.cargo || '',
    fechaIngreso: empleado?.fechaIngreso || '', tipoContrato: empleado?.tipoContrato || 'indefinido',
    terminoContrato: empleado?.terminoContrato || '', afp: empleado?.afp || '', salud: empleado?.salud || 'FONASA',
    planIsapreUF: empleado?.planIsapreUF != null ? String(empleado.planIsapreUF).replace('.', ',') : '',
    sueldoBase: empleado?.sueldoBase != null ? FS.fmtMiles(String(empleado.sueldoBase)) : '',
    jornadaHoras: empleado?.jornadaHoras != null ? String(empleado.jornadaHoras) : '',
    gratificacion: empleado?.gratificacion || 'art50', activo: empleado?.activo ?? true,
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: ev.target.value });
  const esIsapre = f.salud.trim() && f.salud.trim().toUpperCase() !== 'FONASA';

  const elegirUsuario = (id: string) => {
    setUsuarioId(id);
    const u = usuarios.find((x) => x.id === id);
    if (u) setF({ ...f, nombre: u.nombre || f.nombre, cargo: f.cargo || CARGO_POR_ROL[u.rol] || '' });
  };
  const guardar = async () => {
    if (!usuarioId) { setError('Elige al trabajador.'); return; }
    if (!f.nombre.trim()) { setError('Falta el nombre.'); return; }
    setGuardando(true); setError('');
    const cuerpo: Partial<CT.Empleado> = {
      nombre: f.nombre, usuarioId: usuarioId === OTRA ? null : usuarioId, rut: f.rut || null, cargo: f.cargo, fechaIngreso: f.fechaIngreso || null,
      tipoContrato: f.tipoContrato as CT.Empleado['tipoContrato'],
      terminoContrato: f.tipoContrato === 'indefinido' ? null : f.terminoContrato || null,
      afp: f.afp || null, salud: f.salud.trim() || 'FONASA',
      planIsapreUF: esIsapre && f.planIsapreUF ? Number(f.planIsapreUF.replace(',', '.')) : null,
      sueldoBase: aNumero(f.sueldoBase), jornadaHoras: aNumero(f.jornadaHoras),
      gratificacion: f.gratificacion as CT.Empleado['gratificacion'], activo: f.activo,
    };
    try { onGuardado(empleado ? await CT.editarEmpleado(empleado.id, cuerpo) : await CT.crearEmpleado(cuerpo)); }
    catch (e: any) { setError(e?.message || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };

  const campo = (label: string, el: React.ReactNode) => (
    <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>{label}</div>{el}</div>
  );
  return (
    <div style={caja}>
      <div style={{ fontWeight: 600, color: claro, marginBottom: 12 }}>{empleado ? 'Datos del empleado' : 'Nuevo empleado'}</div>
      <div className="p-grid3" style={{ marginBottom: 12 }}>
        {campo('Trabajador', <select className="p-select" value={usuarioId} onChange={(e) => elegirUsuario(e.target.value)}>
          <option value="" disabled>Seleccionar usuario…</option>
          {elegibles.map((u) => <option key={u.id} value={u.id}>{u.nombre || u.email}{CARGO_POR_ROL[u.rol] ? ` · ${CARGO_POR_ROL[u.rol]}` : ''}</option>)}
          <option value={OTRA}>Otra persona (sin usuario en Tabularis)</option>
        </select>)}
        {campo('Nombre completo (como va en la liquidación)', <input className="p-input" value={f.nombre} onChange={set('nombre')} disabled={!usuarioId} />)}
        {campo('RUT', <input className="p-input" value={f.rut} onChange={set('rut')} placeholder="12.345.678-9" />)}
        {campo('Cargo', <input className="p-input" value={f.cargo} onChange={set('cargo')} placeholder="Secretaria, procuradora…" />)}
        {campo('Fecha de ingreso', <input className="p-input" type="date" value={f.fechaIngreso} onChange={set('fechaIngreso')} />)}
        {campo('Tipo de contrato', <select className="p-select" value={f.tipoContrato} onChange={set('tipoContrato')}>{Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>)}
        {f.tipoContrato !== 'indefinido' && campo('Término del contrato', <input className="p-input" type="date" value={f.terminoContrato} onChange={set('terminoContrato')} />)}
        {campo('Sueldo base mensual ($)', <input className="p-input" inputMode="numeric" value={f.sueldoBase} onChange={(e) => setF({ ...f, sueldoBase: FS.fmtMiles(e.target.value) })} />)}
        {campo('Jornada semanal (horas)', <input className="p-input" inputMode="numeric" value={f.jornadaHoras} onChange={(e) => setF({ ...f, jornadaHoras: soloDigitos(e.target.value).slice(0, 2) })} placeholder="42" />)}
        {campo('Gratificación', <select className="p-select" value={f.gratificacion} onChange={set('gratificacion')}>{Object.entries(GRATIF).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>)}
        {campo('AFP', <select className="p-select" value={f.afp} onChange={set('afp')}><option value="">Seleccionar…</option>{CT.AFPS.map((a) => <option key={a}>{a}</option>)}</select>)}
        {campo('Salud (FONASA o isapre)', <input className="p-input" value={f.salud} onChange={set('salud')} list="contab-salud" />)}
        {esIsapre && campo('Plan de isapre (UF)', <input className="p-input" inputMode="decimal" value={f.planIsapreUF} onChange={(e) => setF({ ...f, planIsapreUF: e.target.value.replace(/[^\d,]/g, '') })} placeholder="3,5" />)}
      </div>
      <datalist id="contab-salud">{['FONASA', 'Banmédica', 'Colmena', 'Consalud', 'Cruz Blanca', 'Nueva Masvida', 'Vida Tres', 'Esencial'].map((s) => <option key={s} value={s} />)}</datalist>
      {empleado && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: claro, marginBottom: 12 }}>
          <input type="checkbox" checked={f.activo} onChange={(e) => setF({ ...f, activo: e.target.checked })} /> Trabaja en el estudio (desmárcalo si ya se fue; sus documentos se conservan)
        </label>
      )}
      {error && <div style={{ color: '#e57373', fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="p-btn-primary" disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar'}</button>
        {onCancelar && <button className="p-btn-secondary" onClick={onCancelar}>Cancelar</button>}
      </div>
    </div>
  );
};

/** Ficha de un empleado: datos, documentos, cálculo e historial. */
const Empleado: React.FC<{ empleado: CT.Empleado; usuarios: Usuario[]; empleados: CT.Empleado[]; onCambio: () => void }> = ({ empleado, usuarios, empleados, onCambio }) => {
  const [pestana, setPestana] = useState<'documentos' | 'calcular' | 'datos' | 'historial'>('documentos');
  const [docs, setDocs] = useState<CT.DocumentoLaboral[]>([]);
  const cargarDocs = useCallback(() => CT.listarDocumentos(empleado.id).then(setDocs).catch(() => setDocs([])), [empleado.id]);
  useEffect(() => { cargarDocs(); }, [cargarDocs]);

  return (
    <div style={{ ...caja, padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 18, color: claro, fontWeight: 600 }}>{empleado.nombre}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
            {[empleado.rut, empleado.cargo, TIPOS[empleado.tipoContrato], empleado.fechaIngreso && `desde ${FS.fmtDate(empleado.fechaIngreso)}`].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, margin: '14px 0 16px', flexWrap: 'wrap' }}>
        {([['documentos', '📁 Documentos'], ['calcular', '🧮 Calcular liquidación'], ['datos', '✏️ Datos'], ['historial', '🕓 Historial']] as const).map(([id, t]) => (
          <button key={id} className={pestana === id ? 'p-btn-primary' : 'p-btn-secondary'} onClick={() => setPestana(id)} style={{ padding: '6px 12px', fontSize: 12.5 }}>{t}</button>
        ))}
      </div>
      {pestana === 'documentos' && <Documentos empleadoId={empleado.id} docs={docs} onCambio={() => { cargarDocs(); onCambio(); }} />}
      {pestana === 'calcular' && <Calcular empleado={empleado} onIrADatos={() => setPestana('datos')} />}
      {pestana === 'datos' && <FichaEmpleado empleado={empleado} usuarios={usuarios} empleados={empleados} onGuardado={() => { onCambio(); setPestana('documentos'); }} />}
      {pestana === 'historial' && <Historial empleadoId={empleado.id} />}
    </div>
  );
};

const Documentos: React.FC<{ empleadoId: string; docs: CT.DocumentoLaboral[]; onCambio: () => void }> = ({ empleadoId, docs, onCambio }) => {
  const [clase, setClase] = useState<CT.ClaseDocumento>('LIQUIDACION');
  const [periodo, setPeriodo] = useState(mesActual());
  const [fecha, setFecha] = useState('');
  const [liquido, setLiquido] = useState('');
  const [nota, setNota] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const [inputKey, setInputKey] = useState(0);
  const [borrar, setBorrar] = useState<CT.DocumentoLaboral | null>(null);

  const subir = async () => {
    if (!archivo) { setError('Elige el archivo.'); return; }
    if (clase === 'LIQUIDACION' && !periodo) { setError('Indica el mes de la liquidación.'); return; }
    setSubiendo(true); setError('');
    try {
      await CT.subirDocumento(empleadoId, archivo, {
        clase, periodo: clase === 'LIQUIDACION' ? periodo : null, fecha: clase === 'LIQUIDACION' ? null : fecha || null,
        liquido: clase === 'LIQUIDACION' ? aNumero(liquido) : null, nota: nota || null,
      });
      setArchivo(null); setLiquido(''); setNota(''); setFecha(''); setInputKey((k) => k + 1);
      onCambio();
    } catch (e: any) { setError(e?.message || 'No se pudo subir'); }
    finally { setSubiendo(false); }
  };
  const abrir = async (d: CT.DocumentoLaboral, descargar = false) => {
    try { window.open(await CT.urlDocumento(d.id, descargar), '_blank', 'noopener'); }
    catch (e: any) { alert(e?.message || 'No se pudo abrir'); }
  };
  const confirmarBorrar = async () => {
    if (!borrar) return;
    try { await CT.borrarDocumento(borrar.id); setBorrar(null); onCambio(); }
    catch (e: any) { alert(e?.message || 'No se pudo borrar'); }
  };

  return (<>
    <div style={{ ...caja, background: 'rgba(143,180,232,0.06)' }}>
      <div style={{ fontWeight: 600, color: claro, marginBottom: 10, fontSize: 13.5 }}>Subir documento</div>
      <div className="p-grid3" style={{ marginBottom: 10 }}>
        <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Tipo</div>
          <select className="p-select" value={clase} onChange={(e) => setClase(e.target.value as CT.ClaseDocumento)}>{CT.CLASES.map((c) => <option key={c.id} value={c.id}>{c.singular}</option>)}</select></div>
        {clase === 'LIQUIDACION' ? (<>
          <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Mes que liquida</div>
            <input className="p-input" type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} /></div>
          <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Líquido a pagar (opcional, para comparar)</div>
            <input className="p-input" inputMode="numeric" value={liquido} onChange={(e) => setLiquido(FS.fmtMiles(e.target.value))} placeholder="269.297" /></div>
        </>) : (
          <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Fecha del documento</div>
            <input className="p-input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} /></div>
        )}
        <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Nota (opcional)</div>
          <input className="p-input" value={nota} onChange={(e) => setNota(e.target.value)} maxLength={500} /></div>
        <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Archivo</div>
          <input key={inputKey} className="p-input" type="file" accept=".pdf,image/*,.doc,.docx" onChange={(e) => setArchivo(e.target.files?.[0] || null)} /></div>
      </div>
      {error && <div style={{ color: '#e57373', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      <button className="p-btn-primary" disabled={subiendo} onClick={subir}>{subiendo ? 'Subiendo…' : '＋ Subir'}</button>
    </div>

    {docs.length === 0 && <div className="p-empty">Todavía no hay documentos de este empleado.</div>}
    {CT.CLASES.map((c) => {
      const lista = docs.filter((d) => d.clase === c.id);
      if (!lista.length) return null;
      return (
        <div key={c.id} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>{c.titulo} ({lista.length})</div>
          <div style={{ overflowX: 'auto' }}><table className="p-table" style={{ fontSize: 13 }}><tbody>
            {lista.map((d) => (
              <tr key={d.id}>
                <td style={{ whiteSpace: 'nowrap', color: claro, fontWeight: 600 }}>{d.clase === 'LIQUIDACION' ? etiquetaMes(d.periodo) : d.fecha ? FS.fmtDate(d.fecha) : '—'}</td>
                <td>{d.nombre}{d.nota && <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{d.nota}</div>}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right', color: claro }}>{d.liquido != null ? `Líquido ${FS.fmtMoney(d.liquido)}` : ''}</td>
                <td style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{d.subidoPor || '—'} · {FS.fmtDate(d.creado)}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button className="p-btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => abrir(d)}>Ver</button>{' '}
                  <button className="p-btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => abrir(d, true)}>Descargar</button>{' '}
                  <button className="p-btn-danger-sm" onClick={() => setBorrar(d)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody></table></div>
        </div>
      );
    })}

    {borrar && (
      <div style={{ ...caja, borderColor: 'rgba(229,115,115,0.5)' }}>
        <div style={{ color: claro, fontSize: 13.5, marginBottom: 10 }}>
          ¿Borrar {singular(borrar.clase).toLowerCase()} «{borrar.nombre}»? Deja de verse aquí, pero queda en el historial quién lo borró y cuándo.
        </div>
        <button className="p-btn-primary" onClick={confirmarBorrar} style={{ background: '#c0504d' }}>Sí, borrar</button>{' '}
        <button className="p-btn-secondary" onClick={() => setBorrar(null)}>Cancelar</button>
      </div>
    )}
  </>);
};

const Calcular: React.FC<{ empleado: CT.Empleado; onIrADatos: () => void }> = ({ empleado, onIrADatos }) => {
  const [periodo, setPeriodo] = useState(mesActual());
  const [e, setE] = useState({
    diasTrabajados: String(CT.diasDelMes(empleado, mesActual())), horasExtra: '', bonosImponibles: '', noImponibles: '', anticipos: '', otrosDescuentos: '',
    sueldoBase: empleado.sueldoBase != null ? FS.fmtMiles(String(empleado.sueldoBase)) : '',
  });
  const [contrato, setContrato] = useState<CT.Empleado['tipoContrato']>(empleado.tipoContrato);
  const [r, setR] = useState<CT.Calculo | null>(null);
  const [error, setError] = useState('');
  const [calculando, setCalculando] = useState(false);
  const faltan = [!empleado.jornadaHoras && 'la jornada semanal', !empleado.afp && 'la AFP'].filter(Boolean) as string[];

  const correr = async () => {
    setCalculando(true); setError('');
    try {
      setR(await CT.calcular(empleado.id, {
        periodo, diasTrabajados: Number(e.diasTrabajados || 30), horasExtra: Number(e.horasExtra.replace(',', '.') || 0),
        sueldoBase: aNumero(e.sueldoBase), tipoContrato: contrato,
        bonosImponibles: aNumero(e.bonosImponibles) ?? 0, noImponibles: aNumero(e.noImponibles) ?? 0,
        anticipos: aNumero(e.anticipos) ?? 0, otrosDescuentos: aNumero(e.otrosDescuentos) ?? 0,
      }));
    } catch (x: any) { setError(x?.message || 'No se pudo calcular'); setR(null); }
    finally { setCalculando(false); }
  };

  const plata = (k: keyof typeof e, label: string) => (
    <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>{label}</div>
      <input className="p-input" inputMode="numeric" value={e[k]} onChange={(ev) => setE({ ...e, [k]: FS.fmtMiles(ev.target.value) })} placeholder="0" /></div>
  );
  const Tabla: React.FC<{ titulo: string; lineas: CT.Linea[]; total?: [string, number] }> = ({ titulo, lineas, total }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>{titulo}</div>
      <table className="p-table" style={{ fontSize: 13, margin: 0 }}><tbody>
        {lineas.length === 0 && <tr><td style={{ color: 'var(--text-muted)' }}>—</td><td /></tr>}
        {lineas.map((l, i) => <tr key={i}><td>{l.nombre}{l.detalle && <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}> · {l.detalle}</span>}</td><td style={{ textAlign: 'right', color: claro, fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(l.monto)}</td></tr>)}
        {total && <tr style={{ fontWeight: 700 }}><td>{total[0]}</td><td style={{ textAlign: 'right', color: claro, fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(total[1])}</td></tr>}
      </tbody></table>
    </div>
  );

  const dif = r?.contadora ? r.resultado.liquido - r.contadora.liquido : null;
  return (<>
    {faltan.length > 0 && (
      <div className="p-info-box" style={{ marginBottom: 12 }}>
        Para calcular falta {faltan.join(', ')}. <button className="p-btn-secondary" style={{ padding: '3px 10px', fontSize: 12, marginLeft: 6 }} onClick={onIrADatos}>Completar datos</button>
      </div>
    )}
    <div className="p-grid3" style={{ marginBottom: 12 }}>
      <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Mes</div>
        <input className="p-input" type="month" value={periodo} onChange={(ev) => { setPeriodo(ev.target.value); setE({ ...e, diasTrabajados: String(CT.diasDelMes(empleado, ev.target.value)) }); setR(null); }} /></div>
      <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Sueldo base ese mes ($)</div>
        <input className="p-input" inputMode="numeric" value={e.sueldoBase} onChange={(ev) => setE({ ...e, sueldoBase: FS.fmtMiles(ev.target.value) })} /></div>
      <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Contrato ese mes</div>
        <select className="p-select" value={contrato} onChange={(ev) => setContrato(ev.target.value as CT.Empleado['tipoContrato'])}>{Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
      <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Días trabajados</div>
        <input className="p-input" inputMode="numeric" value={e.diasTrabajados} onChange={(ev) => setE({ ...e, diasTrabajados: soloDigitos(ev.target.value).slice(0, 2) })} /></div>
      <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Horas extra</div>
        <input className="p-input" inputMode="decimal" value={e.horasExtra} onChange={(ev) => setE({ ...e, horasExtra: ev.target.value.replace(/[^\d,]/g, '') })} placeholder="0" /></div>
      {plata('bonosImponibles', 'Bonos imponibles ($)')}
      {plata('noImponibles', 'Colación, movilización ($)')}
      {plata('anticipos', 'Anticipos ($)')}
      {plata('otrosDescuentos', 'Otros descuentos ($)')}
    </div>
    {error && <div style={{ color: '#e57373', fontSize: 13, marginBottom: 8 }}>{error}</div>}
    <button className="p-btn-primary" disabled={calculando || faltan.length > 0} onClick={correr}>{calculando ? 'Calculando…' : 'Calcular'}</button>

    {r && (
      <div style={{ marginTop: 18 }}>
        {r.contadora ? (
          <div style={{ ...caja, borderColor: dif === 0 ? 'rgba(106,173,106,0.5)' : 'rgba(224,166,74,0.6)' }}>
            <div style={{ color: dif === 0 ? '#6aad6a' : '#E0A64A', fontWeight: 600, fontSize: 14 }}>
              {dif === 0 ? '✓ Cuadra con la liquidación de la contadora' : `⚠ No cuadra: la diferencia es de ${FS.fmtMoney(Math.abs(dif!))}`}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Contadora ({r.contadora.nombre}): {FS.fmtMoney(r.contadora.liquido)} · Tabularis: {FS.fmtMoney(r.resultado.liquido)}
            </div>
          </div>
        ) : (
          <div className="p-info-box" style={{ marginBottom: 12 }}>
            No hay una liquidación de {etiquetaMes(periodo)} con el líquido anotado. Súbela en Documentos con su líquido a pagar y aquí se comparan solas.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <div>
            <Tabla titulo="Haberes imponibles" lineas={r.resultado.haberes} total={['Total imponible', r.resultado.imponible]} />
            <Tabla titulo="No imponibles" lineas={r.resultado.noImponibles} />
          </div>
          <div>
            <Tabla titulo="Descuentos" lineas={r.resultado.descuentos} total={['Total descuentos', r.resultado.totalDescuentos]} />
            <div style={{ ...caja, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: claro }}>
              <span>Líquido a pagar</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(r.resultado.liquido)}</span>
            </div>
          </div>
          <div>
            <Tabla titulo="Aportes del estudio (no salen del sueldo)" lineas={r.resultado.aportes} total={['Costo total para el estudio', r.resultado.costoEmpresa]} />
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Base tributable {FS.fmtMoney(r.resultado.tributable)} · UF {r.indicadores.uf.toLocaleString('es-CL')} del {FS.fmtDate(r.indicadores.ufFecha)} · UTM {FS.fmtMoney(r.indicadores.utm)}<br />
              Indicadores: {r.indicadores.fuente}.{!r.indicadores.verificado && ' Para este mes no están verificados: revisa los aportes del estudio.'}
            </div>
          </div>
        </div>
        {r.resultado.observaciones.length > 0 && (
          <div className="p-info-box" style={{ marginTop: 8 }}>{r.resultado.observaciones.map((o) => <div key={o}>{o}</div>)}</div>
        )}
      </div>
    )}
  </>);
};

/** La planilla de Previred del mes: lo que hay que pagar por institución,
 *  para compararlo con la "Distribución de Pagos Imposiciones" de Previred. */
const PreviredMes: React.FC<{ empleados: CT.Empleado[] }> = ({ empleados }) => {
  const [periodo, setPeriodo] = useState(mesActual());
  type Fila = { incluir: boolean; dias: string; sueldo: string; contrato: CT.Empleado['tipoContrato'] };
  const inicial = useCallback((p: string): Record<string, Fila> => Object.fromEntries(empleados.map((e) => [e.id, {
    incluir: e.activo && CT.trabajabaEn(e, p), dias: String(CT.diasDelMes(e, p)),
    sueldo: e.sueldoBase != null ? FS.fmtMiles(String(e.sueldoBase)) : '', contrato: e.tipoContrato,
  }])), [empleados]);
  const [filas, setFilas] = useState<Record<string, Fila>>(() => inicial(mesActual()));
  const [r, setR] = useState<CT.Previred | null>(null);
  const [comprobante, setComprobante] = useState('');
  const [error, setError] = useState('');
  const [calculando, setCalculando] = useState(false);
  const cambiar = (id: string, f: Partial<Fila>) => { setFilas({ ...filas, [id]: { ...filas[id], ...f } }); setR(null); };

  const correr = async () => {
    const elegidos = empleados.filter((e) => filas[e.id]?.incluir);
    if (!elegidos.length) { setError('Marca al menos un empleado.'); return; }
    setCalculando(true); setError('');
    try {
      setR(await CT.previred(periodo, elegidos.map((e) => ({
        empleadoId: e.id, diasTrabajados: Number(filas[e.id].dias || 30), sueldoBase: aNumero(filas[e.id].sueldo), tipoContrato: filas[e.id].contrato,
      }))));
    } catch (x: any) { setError(x?.message || 'No se pudo calcular'); setR(null); }
    finally { setCalculando(false); }
  };
  const pagado = aNumero(comprobante);
  const dif = r && pagado != null ? r.total - pagado : null;

  if (!empleados.length) return <div className="p-empty">Agrega primero a los empleados.</div>;
  return (<>
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
      <div className="p-field" style={{ margin: 0 }}><div className="p-field-label" style={{ fontSize: 11 }}>Mes</div>
        <input className="p-input" type="month" value={periodo} onChange={(ev) => { setPeriodo(ev.target.value); setFilas(inicial(ev.target.value)); setR(null); }} /></div>
    </div>
    <div style={{ overflowX: 'auto', marginBottom: 12 }}><table className="p-table" style={{ fontSize: 13 }}>
      <thead><tr><th></th><th>Empleado</th><th>Días</th><th>Sueldo base ese mes</th><th>Contrato ese mes</th></tr></thead>
      <tbody>{empleados.map((e) => { const f = filas[e.id]; if (!f) return null; return (
        <tr key={e.id} style={{ opacity: f.incluir ? 1 : 0.5 }}>
          <td><input type="checkbox" checked={f.incluir} onChange={(ev) => cambiar(e.id, { incluir: ev.target.checked })} /></td>
          <td style={{ color: claro }}>{e.nombre}</td>
          <td><input className="p-input" style={{ width: 64 }} inputMode="numeric" value={f.dias} onChange={(ev) => cambiar(e.id, { dias: soloDigitos(ev.target.value).slice(0, 2) })} /></td>
          <td><input className="p-input" style={{ width: 130 }} inputMode="numeric" value={f.sueldo} onChange={(ev) => cambiar(e.id, { sueldo: FS.fmtMiles(ev.target.value) })} /></td>
          <td><select className="p-select" value={f.contrato} onChange={(ev) => cambiar(e.id, { contrato: ev.target.value as CT.Empleado['tipoContrato'] })}>{Object.entries(TIPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
        </tr>); })}</tbody>
    </table></div>
    {error && <div style={{ color: '#e57373', fontSize: 13, marginBottom: 8 }}>{error}</div>}
    <button className="p-btn-primary" disabled={calculando} onClick={correr}>{calculando ? 'Calculando…' : 'Calcular planilla'}</button>

    {r && (
      <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>Distribución de pagos · {etiquetaMes(periodo)}</div>
          <table className="p-table" style={{ fontSize: 13, margin: 0 }}><tbody>
            {r.distribucion.map((f) => (
              <tr key={f.grupo + f.nombre}><td style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>{f.grupo}</td><td>{f.nombre}</td>
                <td style={{ textAlign: 'right', color: claro, fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(f.monto)}</td></tr>
            ))}
            <tr style={{ fontWeight: 700 }}><td colSpan={2}>Total a pagar</td><td style={{ textAlign: 'right', color: claro, fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(r.total)}</td></tr>
          </tbody></table>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
            UF {r.indicadores.uf.toLocaleString('es-CL')} del {FS.fmtDate(r.indicadores.ufFecha)} · Indicadores: {r.indicadores.fuente}.{!r.indicadores.verificado && ' Para este mes no están verificados.'}
          </div>
        </div>
        <div>
          <div className="p-field" style={{ margin: '0 0 10px' }}><div className="p-field-label" style={{ fontSize: 11 }}>Total a pagar según el comprobante de Previred</div>
            <input className="p-input" inputMode="numeric" value={comprobante} onChange={(ev) => setComprobante(FS.fmtMiles(ev.target.value))} placeholder="133.886" /></div>
          {dif !== null && (
            <div style={{ ...caja, borderColor: dif === 0 ? 'rgba(106,173,106,0.5)' : 'rgba(224,166,74,0.6)', color: dif === 0 ? '#6aad6a' : '#E0A64A', fontWeight: 600, fontSize: 14 }}>
              {dif === 0 ? '✓ Cuadra con Previred' : `⚠ No cuadra: Tabularis da ${FS.fmtMoney(Math.abs(dif))} ${dif > 0 ? 'más' : 'menos'}`}
            </div>
          )}
          <table className="p-table" style={{ fontSize: 13, margin: 0 }}>
            <thead><tr><th>Empleado</th><th style={{ textAlign: 'right' }}>Imponible</th><th style={{ textAlign: 'right' }}>Líquido</th><th style={{ textAlign: 'right' }}>Costo estudio</th></tr></thead>
            <tbody>{r.empleados.map((x) => (
              <tr key={x.empleadoId}><td>{x.nombre}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(x.imponible)}</td>
                <td style={{ textAlign: 'right', color: claro, fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(x.liquido)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{FS.fmtMoney(x.costoEmpresa)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    )}
  </>);
};

const Historial: React.FC<{ empleadoId?: string }> = ({ empleadoId }) => {
  const [filas, setFilas] = useState<CT.EntradaHistorial[] | null>(null);
  useEffect(() => { CT.historial(empleadoId).then(setFilas).catch(() => setFilas([])); }, [empleadoId]);
  const texto = useMemo(() => (h: CT.EntradaHistorial) => {
    const accion = ACCIONES[h.accion] || h.accion;
    if (!h.documento) return <>{accion}{!empleadoId && <> <b style={{ color: claro }}>{h.empleado}</b></>}</>;
    const que = `${singular(h.documento.clase).toLowerCase()}${h.documento.periodo ? ` de ${etiquetaMes(h.documento.periodo)}` : ''}`;
    return <>{accion} {que} <span style={{ color: 'var(--text-muted)' }}>«{h.documento.nombre}»</span>{!empleadoId && <> de <b style={{ color: claro }}>{h.empleado}</b></>}</>;
  }, [empleadoId]);
  if (!filas) return <div className="p-empty">Cargando…</div>;
  if (!filas.length) return <div className="p-empty">Todavía no hay movimientos.</div>;
  return (
    <div style={{ overflowX: 'auto' }}><table className="p-table" style={{ fontSize: 13 }}><tbody>
      {filas.map((h, i) => (
        <tr key={i}>
          <td style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{cuando(h.cuando)}</td>
          <td><b style={{ color: claro }}>{h.quien}</b> {texto(h)}</td>
        </tr>
      ))}
    </tbody></table></div>
  );
};

export default Contabilidad;
